import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { get } from 'api';
import { post } from 'api';
import moment from 'moment';
import { createPayrollPolicySchema } from 'schema/createPayrollPolicySchema';

export default function PayrollPolicyForm() {
  const [currentPolicy, setCurrentPolicy] = useState({});

  // const getFirstDayOfMonth = () => {
  //   const today = new Date();
  //   return new Date(today.getFullYear(), today.getMonth(), 1);
  // };
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(createPayrollPolicySchema),
    defaultValues: {
      pfEnabled: false,
      esicEnabled: false,
      professionalTaxEnabled: false,
      plCarryForward: false,
      isActive: true,
      effectiveFrom: moment().startOf('year').format('YYYY-MM-DD'),
    },
  });

  const fetchCurrentPolicy = async () => {
    const res = await get('admin/currentPayrollPolicy');
    if (res.status == 200) {
      setCurrentPolicy(res.data);
    } else {
      setCurrentPolicy(null);
    }
  };

  useEffect(() => {
    fetchCurrentPolicy();
  }, []);

  useEffect(() => {
    if (!currentPolicy) return;
    reset({
      ...currentPolicy,
      effectiveFrom: currentPolicy.effectiveFrom
        ? new Date(currentPolicy.effectiveFrom).toISOString().split('T')[0]
        : '',
    });
  }, [currentPolicy, reset]);

  const pfEnabled = watch('pfEnabled');
  const esicEnabled = watch('esicEnabled');
  const professionalTaxEnabled = watch('professionalTaxEnabled');
  const plCarryForward = watch('plCarryForward');

  const onSubmit = async (data) => {
    const res = await post('/admin/createPayrollPolicy', data);

    if (res.status == 200) {
      fetchCurrentPolicy();
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 5 }}>
      <Typography variant="h4" fontWeight={600} mb={4}>
        Create Payroll Policy
      </Typography>

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* BASIC INFO */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" mb={3}>
              Basic Information
            </Typography>

            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Policy Name"
                  fullWidth
                  InputLabelProps={{
                    shrink: Boolean(field.value) || field.value === 0,
                  }}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              )}
            />
          </CardContent>
        </Card>

        {/* SALARY STRUCTURE */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" mb={3}>
              Salary Structure (% of CTC)
            </Typography>

            <Grid container spacing={3}>
              {/* {['basicPercent', 'hraPercent', 'specialAllowancePercent'].map((fieldName) => ( */}
              <Grid item size={{ xs: 12, md: 4 }} key="basicPercent">
                <Controller
                  name="basicPercent"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type="number"
                      label="Basic (% of Monthly CTC)"
                      fullWidth
                      InputLabelProps={{
                        shrink: Boolean(field.value) || field.value === 0,
                      }}
                      error={!!errors.basicPercent}
                      helperText={errors.basicPercent?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 4 }} key="hraPercent">
                <Controller
                  name="hraPercent"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type="number"
                      label="HRA (% of Basic)"
                      fullWidth
                      InputLabelProps={{
                        shrink: Boolean(field.value) || field.value === 0,
                      }}
                      error={!!errors.hraPercent}
                      helperText={errors.hraPercent?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 4 }} key="specialAllowancePercent">
                <Controller
                  name="specialAllowancePercent"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type="number"
                      label="Sp. Allowance (% of Basic)"
                      fullWidth
                      InputLabelProps={{
                        shrink: Boolean(field.value) || field.value === 0,
                      }}
                      error={!!errors.specialAllowancePercent}
                      helperText={errors.specialAllowancePercent?.message}
                    />
                  )}
                />
              </Grid>
              {/*  ))} */}
            </Grid>
            {errors.salaryStructureError && (
              <Typography color="error" sx={{ mt: 2 }}>
                {errors.salaryStructureError.message}
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* STATUTORY */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" mb={3}>
              Statutory Deductions
            </Typography>

            {/* Professional Tax */}
            <Grid container spacing={3}>
              <Grid item size={{ xs: 12, md: 6 }} key="professionalTaxEnabled">
                <FormControlLabel
                  control={
                    <Controller
                      name="professionalTaxEnabled"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          {...field}
                          checked={field.value || false}
                          error={!!errors.professionalTaxEnabled}
                          helperText={errors.professionalTaxEnabled?.message}
                          sx={{ mr: 2 }}
                        />
                      )}
                    />
                  }
                  label="Enable Professional Tax"
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 6 }} key="professionalTaxAmount">
                {professionalTaxEnabled && (
                  <Controller
                    name="professionalTaxAmount"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Professional Tax Amount"
                        type="number"
                        fullWidth
                        error={!!errors.professionalTaxAmount}
                        helperText={errors.professionalTaxAmount?.message}
                      />
                    )}
                  />
                )}
              </Grid>
            </Grid>
            <Divider sx={{ my: 3 }} />

            {/* PF */}
            <Grid container spacing={3}>
              <Grid item size={{ xs: 12, md: 6 }} key="pfEnabled">
                <FormControlLabel
                  control={
                    <Controller
                      name="pfEnabled"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          {...field}
                          checked={field.value || false}
                          sx={{ mr: 2 }}
                          error={!!errors.pfEnabled}
                          helperText={errors.pfEnabled?.message}
                        />
                      )}
                    />
                  }
                  label="Enable PF"
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 6 }} key="pf">
                {pfEnabled && (
                  <Grid container spacing={2} mt={1}>
                    <Grid item size={{ xs: 6 }}>
                      <Controller
                        name="pfEmployeePercent"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Employee %"
                            type="number"
                            fullWidth
                            error={!!errors.pfEmployeePercent}
                            helperText={errors.pfEmployeePercent?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item size={{ xs: 6 }}>
                      <Controller
                        name="pfEmployerPercent"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Employer %"
                            type="number"
                            fullWidth
                            error={!!errors.pfEmployerPercent}
                            helperText={errors.pfEmployerPercent?.message}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Divider sx={{ my: 3 }} />

            {/* ESIC */}
            <Grid container spacing={3}>
              <Grid item size={{ xs: 12, md: 6 }} key="pfEnabled">
                <FormControlLabel
                  control={
                    <Controller
                      name="esicEnabled"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          {...field}
                          checked={field.value || false}
                          sx={{ mr: 2 }}
                          error={!!errors.esicEnabled}
                          helperText={errors.esicEnabled?.message}
                        />
                      )}
                    />
                  }
                  label="Enable ESIC"
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 6 }} key="esic">
                {esicEnabled && (
                  <Grid container spacing={2} mt={1}>
                    <Grid item size={{ xs: 6 }}>
                      <Controller
                        name="esicEmployeePercent"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Employee %"
                            type="number"
                            fullWidth
                            error={!!errors.esicEmployeePercent}
                            helperText={errors.esicEmployeePercent?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item size={{ xs: 6 }}>
                      <Controller
                        name="esicEmployerPercent"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Employer %"
                            fullWidth
                            type="number"
                            error={!!errors.esicEmployerPercent}
                            helperText={errors.esicEmployerPercent?.message}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* LEAVE RULES */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" mb={3}>
              Leave Rules
            </Typography>

            <Grid container spacing={3}>
              <Grid item size={{ xs: 6 }}>
                <Controller
                  name="slPerYear"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Sick Leaves / Year"
                      type="number"
                      fullWidth
                      InputLabelProps={{
                        shrink: Boolean(field.value) || field.value === 0,
                      }}
                      error={!!errors.slPerYear}
                      helperText={errors.slPerYear?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item size={{ xs: 6 }}>
                <Controller
                  name="plPerYear"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Paid Leaves / Year"
                      fullWidth
                      type="number"
                      error={!!errors.plPerYear}
                      helperText={errors.plPerYear?.message}
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />
            <Grid container spacing={3}>
              <Grid item size={{ xs: 12, md: 6 }} key="plCarryForward">
                <FormControlLabel
                  control={
                    <Controller
                      name="plCarryForward"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          {...field}
                          checked={field.value || false}
                          sx={{ mr: 2 }}
                          error={!!errors.plCarryForward}
                          helperText={errors.plCarryForward?.message}
                        />
                      )}
                    />
                  }
                  label="Allow PL Carry Forward"
                />
              </Grid>
              <Grid item size={{ xs: 12, md: 6 }} key="pfEnabled">
                {plCarryForward && (
                  <Controller
                    name="maxPlCarryForward"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Max PL Carry Forward"
                        fullWidth
                        type="number"
                        sx={{ mt: 2 }}
                        error={!!errors.maxPlCarryForward}
                        helperText={errors.maxPlCarryForward?.message}
                      />
                    )}
                  />
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* DATES */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" mb={3}>
              Effective Period
            </Typography>

            <Grid container spacing={3}>
              <Grid item size={{ xs: 6 }}>
                <Controller
                  name="effectiveFrom"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type="date"
                      label="Effective From"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      disabled={true}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Button
          variant="contained"
          size="large"
          type="submit"
          fullWidth
          sx={{ py: 1.5, fontWeight: 600 }}
        >
          Save Policy
        </Button>
      </form>
    </Container>
  );
}
